import { showToast } from '../utils/showToast';

const rawBase = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const BASE_URL = (typeof rawBase === 'string' ? rawBase.trim() : rawBase).replace(/\/+$/, '');

const buildUrl = (path) => {
  const safePath = String(path || '').replace(/^\/+/, '/');
  if (!BASE_URL) return safePath;
  return `${BASE_URL}${safePath}`;
};

let isForcingLogout = false;

function forceLogout(message) {
  if (isForcingLogout) return;
  isForcingLogout = true;

  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  const logoutMessage = message || 'Tu sesion expiro, vuelve a iniciar sesion.';
  try {
    showToast({ type: 'warning', message: logoutMessage });
  } catch (error) {
    console.warn(logoutMessage);
  }

  if (typeof window !== 'undefined') {
    setTimeout(() => window.location.replace('/'), 150);
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Reintentos para fallos de RED (fetch nunca llegó a obtener respuesta del servidor,
// p.ej. TypeError: Failed to fetch por un hipo de conexión) Y para 502/503/504, que son
// la firma típica de un corte de red/proxy transitorio (el servidor Node nunca llega a
// ver la petición, Caddy/el enlace responde por él). Nunca se reintenta si el servidor
// respondió con un error de aplicación (4xx que no sea por gateway, 500, etc.), y nunca
// para métodos no-idempotentes (evita duplicar una acción como crear/pagar/enviar si el
// reintento coincide con que el POST sí llegó).
const RETRYABLE_METHODS = new Set(['GET']);
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const MAX_NETWORK_RETRIES = 3;
const RETRY_DELAYS_MS = [500, 1200, 2500];

async function request(path, { method = 'GET', headers = {}, body, params, ...rest } = {}) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  let resolvedPath = path;
  if (params && typeof params === 'object') {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    if (qs) resolvedPath = `${path}${path.includes('?') ? '&' : '?'}${qs}`;
  }

  const fetchOpts = {
    method,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? (isFormData ? body : JSON.stringify(body)) : undefined,
    ...rest,
  };

  const canRetry = RETRYABLE_METHODS.has(String(method).toUpperCase());
  let res;
  let attempt = 0;
  for (;;) {
    try {
      res = await fetch(buildUrl(resolvedPath), fetchOpts);
      if (canRetry && RETRYABLE_STATUS.has(res.status) && attempt < MAX_NETWORK_RETRIES) {
        await sleep(RETRY_DELAYS_MS[attempt] || 2500);
        attempt += 1;
        continue;
      }
      break;
    } catch (networkError) {
      // fetch() solo lanza cuando la petición nunca obtuvo respuesta (red caída, CORS
      // preflight fallido, DNS, conexión cortada a medias, etc.) — no cuando el servidor
      // responde con un error HTTP.
      if (!canRetry || attempt >= MAX_NETWORK_RETRIES) throw networkError;
      await sleep(RETRY_DELAYS_MS[attempt] || 2500);
      attempt += 1;
    }
  }

  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    try { data = await res.text(); } catch { data = null; }
  }

  if (!res.ok) {
    const message = (data && (data.message || data.error)) || res.statusText || 'Request failed';
    if (res.status === 401 && token) {
      forceLogout(message);
    }
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export const httpClient = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
  del: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
  baseUrl: BASE_URL,
};

export default httpClient;
